import React, { useMemo } from 'react';
import { sizes } from '@sharefiledev/antd-config';
import { ResourceTypes } from '@sharefiledev/esign-pilet-sdk';
import { notifyError } from '@sharefiledev/flow-web';
import { useQuery } from '@tanstack/react-query';
import { App, Grid, Space, Spin } from 'antd';
import { AxiosError } from 'axios';
import { useHistory, useLocation } from 'react-router-dom';
import { ComponentTypes } from '../Common/Components/types';
import { getEidasAuthorizationFlag } from '../Common/ComponentsRenderer/util';
import { getSignerToken, isUploadSignatureFeatureEnabled } from '../Common/helper';
import { useSearchParams } from '../Common/Hooks/useSearchParams';
import { redirectToRS } from '../Common/rsRedirection';
import { useAppStore } from '../Common/store';
import { PiletEvents, SignInSource } from '../constants';
import { usePiletApiContext } from '../Contexts/PiletApiContext';
import { handleErrorWithServerMessage } from '../data/errorHandler';
import { ESignatureClient } from '../data/eSignatureClient';
import {
	ComponentValue,
	DocumentStates,
	RSTokenHeaderTypes,
	SignatureResponse,
	SignerStatus,
	SignerSubmissionResponse,
	SignerUnauthorizedError,
	SignerUnauthorizedStates,
} from '../data/rsTypes';
import { signDashboardPath, SignerPageRoutePattern } from '../routes';
import { t } from '../util';
import { deleteCookie, getCookie, KBA_KEY, setCookie } from '../util/cookieUtils';
import { isShareFileMobileClient } from '../util/file';
import { transformSnakeCaseKeysToCamelCase } from '../util/objectUtil';
import { DigiCertSuccessModal } from './Modals/DigiCertSuccessModal';
import { KbaModal } from './Modals/KbaModal/KbaModal';
import { PasscodeModal } from './Modals/Passcode/PasscodeModal';
import { SuccessModal } from './Modals/SuccessModal';
import { TermsAndConditionsModal } from './Modals/TermsAndConditionsModal';
import { UnauthorizedModal } from './Modals/UnauthorizedModal';
import { PageContent } from './PageContent/PageContent';
import { PageHeader } from './PageHeader/PageHeader';
import { SignerPageLayout } from './SignerPageLayout';
import { SubmitResponseButton } from './SubmitResponseButton/SubmitResponseButton';

const DEFAULT_FONT_SIZE = 14;
const pageLoaderStyles: React.CSSProperties = {
	height: 'calc(100vh - 80px)',
	width: '100vw',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
};
const { useBreakpoint } = Grid;

interface SignerPageProps {
	isSignFromDashboardFlow?: boolean;
	signedInUserId?: string | null;
	signedInUserSearchParams?: URLSearchParams | null;
	isSignerPageDrawerOpen?: boolean;
	setIsSignerPageDrawerOpen?: (isOpen: boolean) => void;
	isContentOnlyMode?: boolean;
	cancelInteraction?: () => void;
	containerRID?: string;
	signInSource?: string;
}

export const SignerPage: React.FC<SignerPageProps> = ({
	isSignFromDashboardFlow = false,
	signedInUserId = null,
	signedInUserSearchParams = null,
	isSignerPageDrawerOpen = true,
	setIsSignerPageDrawerOpen,
	isContentOnlyMode = false,
	cancelInteraction,
	containerRID,
	signInSource = SignInSource.WebApp,
}) => {
	const { notification } = App.useApp();
	const [submitResponse, setSubmitResponse] =
		React.useState<SignerSubmissionResponse>(null);
	const screens = useBreakpoint();
	const location = useLocation();
	const history = useHistory();
	const { piletApi } = usePiletApiContext();

	const signerId = isSignFromDashboardFlow
		? signedInUserId
		: SignerPageRoutePattern.match(location.pathname).signerId;
	const defaultSearchParams = useSearchParams(location.search);
	const searchParams = isSignFromDashboardFlow
		? signedInUserSearchParams
		: defaultSearchParams; // This will have identity_token, access_token and kba search params
	const signerToken = useMemo(() => getSignerToken(searchParams), [searchParams]);
	const linkHasAccessToken = signerToken.tokenType === RSTokenHeaderTypes.AccessToken;
	const isKbaProtected =
		!!searchParams.get('kba') && !linkHasAccessToken && !isSignFromDashboardFlow;

	const [isDocumentLocked, setIsDocumentLocked] = React.useState(false);
	const [isKbaLocked, setIsKbaLocked] = React.useState(isKbaProtected);
	const [unauthorizedState, setUnauthorizedState] =
		React.useState<SignerUnauthorizedStates>(null);
	const [senderEmail, setSenderEmail] = React.useState<string>(null);
	const [loading, setLoading] = React.useState(false);
	const [uploadSignatureFeatureEnabled, setUploadSignatureFeatureEnabled] =
		React.useState(true);

	const parts = window.location.host.split('.');
	const subdomain = parts.length >= 3 ? parts[0] : '';

	const unlockDocument = answerDigest => {
		searchParams.append('passcode_answer_digest', answerDigest);
		if (!isSignFromDashboardFlow) {
			history.push({ search: searchParams.toString() });
		}
		setIsDocumentLocked(false);
		refetchSigner();
	};

	const {
		error: signerError,
		isError: errorSignerFetched,
		isLoading: fetchingSigner,
		isSuccess: signerRequestDataFetched,
		isRefetching: refetchingSigner,
		data: signerRequestData,
		refetch: refetchSigner,
	} = useQuery(
		['signer', signerId],
		() => {
			if (!isDocumentLocked) {
				searchParams.delete('passcode_answer_digest');

				const cleanedSearchParams = new URLSearchParams(searchParams.toString());
				if (isSignFromDashboardFlow) {
					cleanedSearchParams.delete('access_token');
				}
				history.push({ search: cleanedSearchParams.toString() });
			}

			return ESignatureClient.getSigner(signerId, searchParams, getCookie(KBA_KEY));
		},
		{
			refetchOnWindowFocus: false,
			retry(_failureCount, error) {
				return !error;
			},
		}
	);

	const baseFile = signerRequestData?.base_files?.[0];
	const account = signerRequestData?.accounts?.[0];

	// TODO: remove the code when the real eIDAS sender configuration will be ready
	const [isEIDASEnabled, setIsEIDASEnabled] = React.useState(false);

	const fetchKbaSession = async (signerIdValue: string, searchParamsValue: any) => {
		const kbaSessionId = getCookie(KBA_KEY);

		if (kbaSessionId) {
			try {
				const response = await ESignatureClient.getKbaSession(
					signerIdValue,
					kbaSessionId,
					searchParamsValue
				);
				// If the response is verify and there are no questions, then delete the cookie. This is to handle refresh when in verify state
				if (response?.action === 'verify' && !response?.questions?.length) {
					deleteCookie(KBA_KEY);
				}
				return response;
			} catch (error) {
				if (error instanceof AxiosError && error.response?.status === 403) {
					setIsKbaLocked(true);
				} else {
					handleErrorWithServerMessage(error);
				}
			}
		} else {
			return ESignatureClient.generateKbaSession(signerIdValue, searchParamsValue);
		}
	};
	const {
		data: kbaSession,
		isSuccess: kbaSessionFetched,
		error: kbaSessionFetchError,
	} = useQuery(
		['kbaSession', signerId, getCookie(KBA_KEY)],
		() => fetchKbaSession(signerId, searchParams),
		{
			refetchOnWindowFocus: false,
			enabled: isKbaLocked,
			retry(_failureCount, error) {
				return !error;
			},
		}
	);

	React.useEffect(() => {
		if (kbaSession?.isSender && !uploadSignatureFeatureEnabled) {
			redirectToRS(false);
		}
	}, [kbaSession?.isSender, uploadSignatureFeatureEnabled]);

	// This will store the component values of components assigned_to the signer and other data
	const [
		setComponentValues,
		setSigner,
		setSignatures,
		setDocumentAttachments,
		setEsignDocument,
		setPersonalSettings,
		setBaseFiles,
		setSignerPayments,
		setSigners,
		setSearchParams,
		setIsSignFromDashboardFlow,
		signer,
		setSignInSource,
		setRsLogoUrl,
	] = useAppStore(state => [
		state.setComponentValues,
		state.setSigner,
		state.setSignatures,
		state.setDocumentAttachments,
		state.setEsignDocument,
		state.setPersonalSettings,
		state.setBaseFiles,
		state.setSignerPayments,
		state.setSigners,
		state.setSearchParams,
		state.setIsSignFromDashboardFlow,
		state.signer,
		state.setSignInSource,
		state.setRsLogoUrl,
	]);

	const redirectToSignatureDashboard = (documentId: string) => {
		const { selectedStatus, searchTerm, adminView } = ((location.state as any)?.data ||
			{}) as {
			selectedStatus: DocumentStates[];
			searchTerm: string;
			adminView: boolean;
		};
		history.push({
			pathname: signDashboardPath,
			state: {
				data: {
					selectedStatus,
					searchTerm,
					adminView,
					updatedDocumentId: documentId,
				},
			},
		});
	};

	const redirectToSignatureDashboardWithDelay = (documentId: string) => {
		setLoading(true);

		// RS requires some time to reflect the updated state of the document so we are required to add this timeout
		setTimeout(() => {
			setLoading(false);
			setIsSignerPageDrawerOpen(false);
			redirectToSignatureDashboard(documentId);
		}, 800);
	};

	const handleCloseDrawerAndEmitUpdate = () => {
		if (!isContentOnlyMode) {
			return;
		}
		cancelInteraction();
		setTimeout(() => {
			piletApi.emit(PiletEvents.updated, {
				resourceType: ResourceTypes.signatureRequest,
				containerRID,
			});
		}, 1000);
	};

	const handleDeclineDocument = (documentId: string, documentName: string) => {
		notification.success({
			message: t(
				'esign-pilet-ui:signerPage.header.documentActions.declineDocumentNotification',
				{ documentName }
			),
			placement: 'topRight',
		});

		handleCloseDrawerAndEmitUpdate();
		if (!isContentOnlyMode) {
			redirectToSignatureDashboardWithDelay(documentId);
		}
	};

	React.useEffect(() => {
		const handlePopState = () => {
			if (isSignFromDashboardFlow && !isContentOnlyMode) {
				setIsSignerPageDrawerOpen(false);
				history.push('/signaturerequests/dashboard');
			}
		};

		window.addEventListener('popstate', handlePopState);

		return () => {
			window.removeEventListener('popstate', handlePopState);
		};
	}, [history, isSignFromDashboardFlow, setIsSignerPageDrawerOpen, isContentOnlyMode]);

	React.useEffect(() => {
		if (errorSignerFetched) {
			if (signerError instanceof AxiosError) {
				if (signerError?.response?.data?.includes('Unauthorized. Requires passcode.')) {
					setIsDocumentLocked(true);
					return;
				}

				// 404 - the signer doesn't have access anymore or typed some random string instead of signerId
				// 401 - identity token is missing or wrong, but the document does not have special status (still pending to sign)
				if (signerError.response.status === 404 || signerError.response.status === 401) {
					setUnauthorizedState(SignerUnauthorizedStates.Invalid);
					return;
				}

				if (
					signerError?.response?.status === 308 &&
					signerError?.response?.data[0]?.includes('This document requires kba')
				) {
					if (isSignFromDashboardFlow) {
						searchParams.append('kba', 'true');
						refetchSigner();
					} else if (linkHasAccessToken) {
						setUnauthorizedState(SignerUnauthorizedStates.KbaRequired);
					}
					return;
				}

				try {
					const errorResponse: SignerUnauthorizedError = JSON.parse(
						signerError?.response?.data[0]
					);

					const regex = new RegExp(Object.values(SignerUnauthorizedStates).join('|'));
					const match = errorResponse.message.match(regex);

					if (match) {
						setUnauthorizedState(match[0] as SignerUnauthorizedStates);
						setSenderEmail(errorResponse.sender_email);
					}
				} catch {
					return;
				}
			}
		}

		// TODO: remove the code when the real eIDAS sender configuration will be ready
		if (getEidasAuthorizationFlag()) {
			setIsEIDASEnabled(true);
		}

		if (signerRequestDataFetched && signerRequestData.documents) {
			const esignDocument = signerRequestData.documents[0];
			const documentComponents = esignDocument.components || [];
			const documentComponentValues = signerRequestData.component_values || [];
			const mergeFieldValues = signerRequestData.mergefield_values || [];
			setRsLogoUrl(
				signerRequestData.accounts?.[0]?.display_logo
					? signerRequestData.accounts?.[0]?.logo_url
					: null
			);
			const isUploadSignatureFeatureEnabledForAccount = isUploadSignatureFeatureEnabled(
				signerRequestData.accounts[0]['created_at']
			);
			const componentValues: ComponentValue[] = [];
			setUploadSignatureFeatureEnabled(isUploadSignatureFeatureEnabledForAccount);
			setSigner(signerRequestData.signer);
			setSigners(signerRequestData.signers);
			setSearchParams(searchParams);
			setIsSignFromDashboardFlow(isSignFromDashboardFlow);
			setSignInSource(
				isShareFileMobileClient() ? SignInSource.ClientMobileApp : signInSource
			);
			setEsignDocument(esignDocument);
			setBaseFiles(signerRequestData.base_files[0]);
			setIsDocumentLocked(false);
			setPersonalSettings({
				allowTypedSignature: esignDocument.sender_allow_typed_signature,
				allowDrawnSignature: esignDocument.sender_allow_drawn_signature,
				allowUploadedSignature:
					esignDocument.sender_allow_uploaded_signature &&
					isUploadSignatureFeatureEnabledForAccount,
			});
			setSignerPayments(signerRequestData.signer_payments);
			if (signerRequestData.attachments) {
				setDocumentAttachments(signerRequestData.attachments);
			}

			const fetchAllSignatures = async (
				signaturePromises: Promise<SignatureResponse>[]
			) => {
				try {
					const response = await Promise.all(signaturePromises);
					const signatures = response.map((res: any) =>
						transformSnakeCaseKeysToCamelCase(res.signature)
					);
					setSignatures(signatures);
				} catch (error) {
					notifyError(t('esign-pilet-ui:signatureRequestErrorMessage'));
				}
			};

			const annotatedSignaturePromises: Promise<SignatureResponse>[] = [];
			const otherSignerSignaturesPromises: Promise<SignatureResponse>[] = [];

			const mergeFieldValuesMap = new Map<string, ComponentValue>(
				mergeFieldValues.map((mfValue: ComponentValue) => [mfValue.component_id, mfValue])
			);
			const documentComponentValuesMap = new Map<string, ComponentValue>(
				documentComponentValues.map(comValue => [comValue.component_id, comValue])
			);

			documentComponents.forEach(component => {
				const componentValue = component.is_merge_field
					? mergeFieldValuesMap.get(component.id)
					: documentComponentValuesMap.get(component.id);
				if (
					component.is_merge_field &&
					component.type === ComponentTypes.TextComponent &&
					component.multiline === true
				) {
					component.value = componentValue?.value || '';
					component.font_size = componentValue?.font_size || DEFAULT_FONT_SIZE;
				}
				if (Boolean(component['signature_id'])) {
					annotatedSignaturePromises.push(
						ESignatureClient.getSignature(
							component['signature_id'],
							esignDocument.id,
							signerToken,
							signerRequestData.signer.id
						)
					);
				}

				// TODO: Check if we need to check signer status here
				if (
					component.type === ComponentTypes.SignatureComponent &&
					esignDocument.signer_sequencing &&
					component.assigned_to !== signerRequestData.signer.role_name &&
					componentValue &&
					componentValue.value
				) {
					otherSignerSignaturesPromises.push(
						ESignatureClient.getSignature(
							componentValue.value,
							esignDocument.id,
							signerToken,
							signerRequestData.signer.id
						)
					);
				}

				// If the component value is not present in the component_values array, then add it as a default value or null
				if (!componentValue) {
					const defaultValue = component.value ?? null;
					componentValues.push({
						component_id: component.id,
						document_id: signerRequestData.documents[0].id,
						id: `cv_${component.id}`,
						value: component.group_id ? 'false' : defaultValue, // fix for checkbox group initial value
						font_size: Number(component.font_size) || DEFAULT_FONT_SIZE,
					});
				} else {
					componentValues.push(componentValue);
				}
			});
			fetchAllSignatures([
				...annotatedSignaturePromises,
				...otherSignerSignaturesPromises,
			]);

			setComponentValues(componentValues);
		}
	}, [
		linkHasAccessToken,
		errorSignerFetched,
		signerError,
		setComponentValues,
		setSigner,
		setSigners,
		setSignatures,
		setDocumentAttachments,
		setEsignDocument,
		signerRequestData,
		signerRequestDataFetched,
		signerToken,
		setPersonalSettings,
		setBaseFiles,
		setSignerPayments,
		refetchSigner,
		searchParams,
		setSearchParams,
		isSignFromDashboardFlow,
		setIsSignFromDashboardFlow,
		signerId,
		signInSource,
		setSignInSource,
		setRsLogoUrl,
	]);

	React.useEffect(() => {
		if (kbaSessionFetchError) {
			if (kbaSessionFetchError instanceof AxiosError) {
				if (kbaSessionFetchError?.response.status === 403) {
					setIsKbaLocked(true);
				}
			}
		}

		if (kbaSessionFetched) {
			if (kbaSession.session && kbaSession.action !== 'index') {
				setCookie(KBA_KEY, kbaSession.session, new Date(kbaSession.expiration));
			} else {
				setIsKbaLocked(false);
			}
		}
	}, [kbaSession, kbaSessionFetchError, kbaSessionFetched]);

	const unlockKbaDocument = () => {
		setIsKbaLocked(false);
		refetchSigner();
	};

	const handleSubmitResponse = (response: SignerSubmissionResponse) => {
		if (response.signer.status === SignerStatus.PendingIdentityAuthentication) {
			refetchSigner();
		}

		handleCloseDrawerAndEmitUpdate();

		setSubmitResponse(response);
	};

	if (fetchingSigner || refetchingSigner || loading) {
		if (isSignFromDashboardFlow && !isContentOnlyMode) {
			return <Spin data-testid="page-spinner" style={{ width: sizes.XL }} />;
		} else {
			return (
				<Space style={pageLoaderStyles}>
					<Spin size="large" data-testid="page-spinner" />
				</Space>
			);
		}
	}

	// TODO: Change true to signerRequestDataFetched condition when getSigner endpoint is updated
	if (!isDocumentLocked && isEIDASEnabled) {
		return (
			<SignerPageLayout>
				<DigiCertSuccessModal
					providerId="d6f787fd-b13a-46f3-b837-1c924a819fc4" // TODO: Replace with the real value from the sender settings
					subdomain={subdomain}
					documentId={signerRequestData ? signerRequestData.documents[0].id : null}
				/>
			</SignerPageLayout>
		);
	}

	if (
		!kbaSession?.isSender &&
		isKbaLocked &&
		signerRequestDataFetched &&
		(kbaSessionFetched || kbaSessionFetchError)
	) {
		return (
			<SignerPageLayout>
				<KbaModal
					kbaLocked={!!kbaSessionFetchError}
					identityToken={signerToken.token}
					kbaSession={kbaSession}
					signerData={signerRequestData}
					unlockDocument={unlockKbaDocument}
				/>
			</SignerPageLayout>
		);
	}

	if (errorSignerFetched && unauthorizedState) {
		return (
			<UnauthorizedModal
				unauthorizedState={unauthorizedState}
				senderEmail={senderEmail}
				signerId={signerId}
			/>
		);
	}

	if (submitResponse) {
		if (isContentOnlyMode) {
			return;
		}
		if (isSignFromDashboardFlow) {
			redirectToSignatureDashboardWithDelay(submitResponse.document.id);
		} else {
			return (
				<SignerPageLayout>
					<SuccessModal
						signerEmail={signerRequestData.signer.email}
						documentState={submitResponse.document.document_state}
						documentId={submitResponse.document.id}
						token={submitResponse.signer.redirect_token}
					/>
				</SignerPageLayout>
			);
		}
	}

	if (errorSignerFetched && isDocumentLocked) {
		return (
			<SignerPageLayout>
				<PasscodeModal
					signerId={signerId}
					searchParams={searchParams}
					setDocumentLock={setIsDocumentLocked}
					unlockDocument={unlockDocument}
				/>
			</SignerPageLayout>
		);
	}

	const renderContent = () => (
		<>
			<PageHeader
				baseFiles={baseFile}
				account={account}
				setSubmitResponse={handleSubmitResponse}
				setIsSignerPageDrawerOpen={setIsSignerPageDrawerOpen}
				handleDeclineDocument={isSignFromDashboardFlow ? handleDeclineDocument : null}
				isContentOnlyMode={isContentOnlyMode}
				cancelInteraction={cancelInteraction}
			/>
			<PageContent baseFiles={baseFile} />
			<TermsAndConditionsModal />
			{!screens.sm && (
				<SubmitResponseButton isMobile setSubmitResponse={handleSubmitResponse} />
			)}
		</>
	);

	if (
		signerRequestDataFetched &&
		!isDocumentLocked &&
		!isKbaLocked &&
		baseFile &&
		account &&
		signer
	) {
		if (!uploadSignatureFeatureEnabled) {
			if (isKbaProtected) {
				redirectToRS(true);
				return null;
			}

			if (isSignFromDashboardFlow && !!searchParams.get('kba')) {
				redirectToRS(false, searchParams, `/signers/${signerId}/sign`);
				return null;
			}
		}

		return isContentOnlyMode ? (
			renderContent()
		) : (
			<SignerPageLayout signerPageDrawerOpen={isSignerPageDrawerOpen}>
				{renderContent()}
			</SignerPageLayout>
		);
	}

	return null;
};
